import React from 'react';
import {
  Box, Button, Flex, FormControl, FormHelperText, FormLabel, IconButton, Input, Select, Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuFileUp, LuPlus, LuX } from 'react-icons/lu';
import { fileMeta } from '../source-files/sourceFilesUI';
import PhoneSheet from './PhoneSheet';
import PhoneConfirmSheet from './PhoneConfirmSheet';

const FIELD = { h: '52px', borderRadius: '12px', borderColor: 'redesign.border', bg: 'redesign.bg.card' };

function Label({ children }) {
  return <FormLabel textStyle="phoneMeta" color="phone.muted" mb="6px">{children}</FormLabel>;
}

/**
 * The phone's statement upload, drawn for `SourceFilesUploader`, which keeps
 * every rule and request. A 52px "Upload statement" opens the system file
 * picker through react-dropzone's hidden multi-file input, so iOS offers
 * Files, Photos and the camera. Once files are picked, a sheet asks only what
 * the upload needs, with native 16px selects: the bank and, optionally,
 * which of its accounts, for a PDF or Excel file; one account for a CSV, TXT
 * or TSV; and the password only for a PDF.
 */
export default function PhoneUploadSheet({
  inputProps,
  onPick,
  files,
  onRemove,
  onCancel,
  needsSource,
  fileType,
  sources,
  sourceGroups,
  selectedSource,
  onSource,
  accountsForSource,
  selectedAccounts,
  onAccounts,
  accounts,
  selectedAccount,
  onAccount,
  password,
  onPassword,
  isReady,
  submitting,
  onUpload,
  duplicate,
}) {
  const { t } = useTranslation(['sourceFiles', 'common']);
  const selectedAccountIds = selectedAccounts.map((a) => String(a.value));
  const sourceOption = (s) => (
    <option key={s.id ?? s.value} value={String(s.id ?? s.value)}>{s.label}</option>
  );

  return (
    <>
      <input {...inputProps} />
      <Button
        variant="phonePrimary"
        w="100%"
        leftIcon={<Box as={LuFileUp} boxSize="20px" />}
        onClick={onPick}
      >
        {t('phone.upload')}
      </Button>

      <PhoneSheet
        id="statement-upload"
        isOpen={files.length > 0}
        onClose={onCancel}
        size="detent"
        headerLeft={(
          <Text textStyle="phoneHeadline" color="redesign.heading" pl="8px" noOfLines={1}>
            {t('phone.upload')}
          </Text>
        )}
        headerRight={(
          <Button variant="ghost" color="phone.link" fontSize="17px" fontWeight="400" px="8px" minH="44px" onClick={onCancel}>
            {t('common:actions.cancel')}
          </Button>
        )}
        footer={(
          <Button variant="phonePrimary" w="100%" onClick={onUpload} isDisabled={!isReady} isLoading={submitting}>
            {files.length > 1 ? t('phone.uploadButtonMany', { count: files.length }) : t('phone.uploadButton')}
          </Button>
        )}
      >
        <Box px="16px" pt="4px">
          {files.map((file, idx) => {
            const meta = fileMeta(file.name.split('.').pop());
            return (
              <Flex
                key={`${file.name}-${idx}`}
                align="center"
                gap="12px"
                minH="64px"
                pl="14px"
                pr="4px"
                mb="8px"
                borderRadius="14px"
                bg="redesign.bg.card"
                border="1px solid"
                borderColor="redesign.border"
              >
                <Box as={meta.icon} boxSize="22px" color={meta.color} flexShrink={0} />
                <Box flex="1" minW={0}>
                  <Text textStyle="phoneRowName" color="redesign.heading" noOfLines={1}>{file.name}</Text>
                  <Text textStyle="phoneMeta" color="phone.muted">
                    {t('phone.fileSize', { size: Math.max(1, Math.round(file.size / 1024)).toLocaleString('en-US') })}
                  </Text>
                </Box>
                <IconButton
                  icon={<LuX size={20} />}
                  variant="ghost"
                  color="phone.muted"
                  aria-label={t('uploader.dropZone.removeAria', { name: file.name })}
                  // 44px in landscape too, where the theme's minimum stops.
                  minW="44px"
                  minH="44px"
                  onClick={() => onRemove(idx)}
                />
              </Flex>
            );
          })}

          <Button
            variant="ghost"
            color="phone.link"
            fontSize="17px"
            fontWeight="500"
            px="0"
            minH="44px"
            leftIcon={<Box as={LuPlus} boxSize="20px" />}
            onClick={onPick}
          >
            {t('phone.addAnother')}
          </Button>

          <Flex direction="column" gap="16px" mt="12px">
            {needsSource ? (
              <>
                {/* Upload stays disabled until a bank is chosen, so no asterisk. */}
                <FormControl>
                  <Label>{t('phone.bank')}</Label>
                  <Select
                    {...FIELD}
                    value={selectedSource ? String(selectedSource.id ?? selectedSource.value) : ''}
                    onChange={(e) => onSource(sources.find((s) => String(s.id ?? s.value) === e.target.value) || null)}
                  >
                    <option value="" disabled>{t('phone.choose')}</option>
                    {/* MI-345: the user's own institutions first, when they have any. */}
                    {sourceGroups ? sourceGroups.map((g) => (
                      <optgroup key={g.label} label={g.label}>
                        {g.options.map(sourceOption)}
                      </optgroup>
                    )) : sources.map(sourceOption)}
                  </Select>
                </FormControl>
                {selectedSource && accountsForSource.length > 0 && (
                  <FormControl>
                    <Label>{t('uploader.fields.restrictAccounts')}</Label>
                    {/* iOS draws a multiple select as one line that opens its
                        own checklist; no sheet is stacked on this one. The
                        chevron says it opens. */}
                    <Select
                      {...FIELD}
                      multiple
                      value={selectedAccountIds}
                      onChange={(e) => {
                        const ids = [...e.target.selectedOptions].map((o) => o.value);
                        onAccounts(accountsForSource.filter((a) => ids.includes(String(a.value))));
                      }}
                    >
                      {accountsForSource.map((a) => (
                        <option key={a.value} value={String(a.value)}>{a.label}</option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            ) : (
              <FormControl isRequired>
                <Label>{t('uploader.fields.account')}</Label>
                <Select
                  {...FIELD}
                  value={selectedAccount ? String(selectedAccount.value) : ''}
                  onChange={(e) => onAccount(accounts.find((a) => String(a.value) === e.target.value) || null)}
                >
                  <option value="" disabled>{t('phone.choose')}</option>
                  {accounts.map((a) => (
                    <option key={a.value} value={String(a.value)}>{a.label}</option>
                  ))}
                </Select>
              </FormControl>
            )}

            {fileType === 'pdf' && (
              <FormControl>
                <Label>{t('phone.passwordLabel')}</Label>
                <Input
                  {...FIELD}
                  type="password"
                  autoComplete="off"
                  // The last field: Return only puts the keyboard away.
                  enterKeyHint="done"
                  value={password}
                  onChange={(e) => onPassword(e.target.value)}
                />
                <FormHelperText textStyle="phoneMeta" color="phone.muted">{t('phone.passwordHint')}</FormHelperText>
              </FormControl>
            )}
          </Flex>
        </Box>
      </PhoneSheet>

      <PhoneConfirmSheet
        isOpen={duplicate.isOpen}
        title={t('uploader.duplicate.title')}
        body={t(duplicate.count === 1 ? 'uploader.duplicate.messageOne' : 'uploader.duplicate.messageOther', { count: duplicate.count })}
        confirmLabel={t('uploader.duplicate.confirm')}
        onConfirm={duplicate.onConfirm}
        onCancel={duplicate.onCancel}
      />
    </>
  );
}
